import { SKILL_CATEGORIES, SkillCategory } from "@/components/skill/constants";
import { Skill } from "@/types/types";

export type SkillPickerTab = "all" | SkillCategory | "uncategorized";

export type SkillPickerSource = "expert" | "kit" | "group";

const UNCATEGORIZED_LABEL = "Uncategorized";

const CATEGORY_SET = new Set<string>(SKILL_CATEGORIES);

const PROJECT_KIT_SKILLS_SECTION = "PROJECT KIT SKILLS";

const SECTION_TITLES: Record<Exclude<SkillPickerSource, "kit">, string> = {
  expert: "EXPERT AI SKILLS",
  group: "GROUP SKILLS",
};

function normalizeCategory(category: string | null): SkillPickerTab {
  if (!category || !CATEGORY_SET.has(category)) {
    return "uncategorized";
  }
  return category as SkillCategory;
}

export function getSkillCategoryLabel(category: string | null): string {
  const tab = normalizeCategory(category);
  if (tab === "uncategorized") {
    return UNCATEGORIZED_LABEL;
  }
  return tab;
}

export function toSkillSummary(skill: Omit<Skill, "creatorKitUuid">): Skill {
  return {
    ...skill,
    creatorKitUuid: null,
  };
}

function filterSkillsBySearch(skills: Skill[], query: string): Skill[] {
  if (!query.trim()) {
    return skills;
  }
  const normalizedQuery = query.toLowerCase();
  return skills.filter((skill) =>
    skill.name.toLowerCase().includes(normalizedQuery),
  );
}

function takeUniqueSkills(skills: Skill[], seenUuids: Set<string>): Skill[] {
  const uniqueSkills: Skill[] = [];

  for (const skill of skills) {
    if (seenUuids.has(skill.uuid)) {
      continue;
    }
    seenUuids.add(skill.uuid);
    uniqueSkills.push(skill);
  }

  return uniqueSkills;
}

function getUniqueSkillsAcrossPools(
  pools: SkillPickerPools,
  searchQuery: string,
): Skill[] {
  const seenUuids = new Set<string>();

  return takeUniqueSkills(
    [
      ...filterSkillsBySearch(pools.expertSkills, searchQuery),
      ...filterSkillsBySearch(pools.kitOnlySkills, searchQuery),
      ...filterSkillsBySearch(pools.groupSkills, searchQuery),
    ],
    seenUuids,
  );
}

export interface SkillPickerPools {
  expertSkills: Skill[];
  kitOnlySkills: Skill[];
  groupSkills: Skill[];
}

export function skillRequiresProjectKitLink(
  source: SkillPickerSource,
): boolean {
  return source === "group";
}

interface SkillPickerGroup {
  title: string;
  skills: Skill[];
  source: SkillPickerSource;
}

interface SkillPickerTabConfig {
  key: SkillPickerTab;
  label: string;
}

function sortSkillsByName(skills: Skill[]): Skill[] {
  return [...skills].sort((a, b) => a.name.localeCompare(b.name));
}

function groupSkillsByCategory(
  skills: Skill[],
): { label: string; tab: SkillPickerTab; skills: Skill[] }[] {
  const byCategory = new Map<SkillPickerTab, Skill[]>();

  for (const skill of skills) {
    const category = normalizeCategory(skill.category);
    const existing = byCategory.get(category) ?? [];
    existing.push(skill);
    byCategory.set(category, existing);
  }

  const groups: { label: string; tab: SkillPickerTab; skills: Skill[] }[] = [];

  for (const category of SKILL_CATEGORIES) {
    const categorySkills = byCategory.get(category);
    if (categorySkills?.length) {
      groups.push({
        label: category,
        tab: category,
        skills: sortSkillsByName(categorySkills),
      });
    }
  }

  const uncategorizedSkills = byCategory.get("uncategorized");
  if (uncategorizedSkills?.length) {
    groups.push({
      label: UNCATEGORIZED_LABEL,
      tab: "uncategorized",
      skills: sortSkillsByName(uncategorizedSkills),
    });
  }

  return groups;
}

export function buildSkillPickerTabs(
  pools: SkillPickerPools,
  searchQuery: string,
): SkillPickerTabConfig[] {
  const allSkills = getUniqueSkillsAcrossPools(pools, searchQuery);

  if (allSkills.length === 0) {
    return [];
  }

  const tabs: SkillPickerTabConfig[] = [{ key: "all", label: "All" }];
  const addedTabs = new Set<SkillPickerTab>(["all"]);

  for (const { tab, label } of groupSkillsByCategory(allSkills)) {
    if (!addedTabs.has(tab)) {
      tabs.push({ key: tab, label });
      addedTabs.add(tab);
    }
  }

  return tabs;
}

function getSkillsForCategory(skills: Skill[], tab: SkillPickerTab): Skill[] {
  if (tab === "all") {
    return sortSkillsByName(skills);
  }
  return sortSkillsByName(
    skills.filter((skill) => normalizeCategory(skill.category) === tab),
  );
}

function appendSourceSection(
  groups: SkillPickerGroup[],
  source: SkillPickerSource,
  skills: Skill[],
) {
  if (skills.length === 0) {
    return;
  }
  groups.push({
    title:
      source === "kit" ? PROJECT_KIT_SKILLS_SECTION : SECTION_TITLES[source],
    skills: sortSkillsByName(skills),
    source,
  });
}

export function buildSkillPickerGroups(
  tab: SkillPickerTab,
  pools: SkillPickerPools,
  searchQuery: string,
  showGroup: boolean,
): SkillPickerGroup[] {
  const filteredExpert = filterSkillsBySearch(pools.expertSkills, searchQuery);
  const filteredKitOnly = filterSkillsBySearch(
    pools.kitOnlySkills,
    searchQuery,
  );
  const filteredGroup = filterSkillsBySearch(pools.groupSkills, searchQuery);

  const seenUuids = new Set<string>();

  const expertSkills = takeUniqueSkills(
    tab === "all" ? filteredExpert : getSkillsForCategory(filteredExpert, tab),
    seenUuids,
  );
  const kitSkills =
    tab === "all"
      ? filteredKitOnly
      : getSkillsForCategory(filteredKitOnly, tab);

  const seenForGroup = new Set(seenUuids);
  for (const skill of kitSkills) {
    seenForGroup.add(skill.uuid);
  }

  const groupSkills = takeUniqueSkills(
    tab === "all" ? filteredGroup : getSkillsForCategory(filteredGroup, tab),
    seenForGroup,
  );

  const groups: SkillPickerGroup[] = [];
  appendSourceSection(groups, "expert", expertSkills);
  appendSourceSection(groups, "kit", kitSkills);
  if (showGroup) {
    appendSourceSection(groups, "group", groupSkills);
  }

  return groups;
}
